import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import {
  fetchWirelessCheckLoginUsers,
  overrideWirelessCheck,
  type WirelessCheckDevice,
  type WirelessCheckLoginUser,
} from "@/pages/manufacturing/wirelessCheckApi";

export function WirelessCheckOverrideModal({
  open,
  device,
  onClose,
  onSuccess,
}: {
  open: boolean;
  device: WirelessCheckDevice | null;
  onClose: () => void;
  onSuccess: (device: WirelessCheckDevice) => void;
}): JSX.Element | null {
  const [users, setUsers] = useState<WirelessCheckLoginUser[]>([]);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [mdmUserId, setMdmUserId] = useState("");
  const [pin, setPin] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMdmUserId("");
    setPin("");
    setError(null);
    setUsersError(null);
    void fetchWirelessCheckLoginUsers()
      .then((result) => setUsers(result.users.filter((user) => user.hasPin)))
      .catch((loadError) => {
        setUsersError(loadError instanceof Error ? loadError.message : String(loadError));
      });
  }, [open]);

  const pinEligibleUsers = useMemo(
    () => users.filter((user) => user.hasPin),
    [users],
  );

  if (!open || device == null) return null;

  const submit = async () => {
    if (mdmUserId === "" || pin === "") {
      setError("Select an administrator and enter their PIN.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await overrideWirelessCheck({
        serial: device.deviceLabelSerial,
        mdmUserId,
        pin,
      });
      onSuccess(result.device);
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader>
          <h2 className="text-lg font-semibold">Administrator override</h2>
          <p className="text-sm text-muted-foreground">
            Manufacturing module administrators can approve wireless check with their mobile login user and PIN. This
            action is recorded for audit.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm">
            Device <span className="font-medium">{device.deviceLabelSerial}</span> —{" "}
            {device.episode.attemptCount} failed check(s).
          </p>
          {usersError != null ? <p className="text-sm text-destructive">{usersError}</p> : null}
          <div>
            <Label htmlFor="wireless-check-override-user">Administrator</Label>
            <select
              id="wireless-check-override-user"
              className="mt-1 w-full rounded border px-2 py-2 text-sm"
              value={mdmUserId}
              onChange={(event) => setMdmUserId(event.target.value)}
              disabled={pending}
            >
              <option value="">Select user…</option>
              {pinEligibleUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName != null && user.displayName.trim() !== ""
                    ? `${user.displayName} (${user.email})`
                    : user.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="wireless-check-override-pin">PIN</Label>
            <Input
              id="wireless-check-override-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(event) => setPin(event.target.value)}
              disabled={pending}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={pending} onClick={() => void submit()}>
              {pending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying…
                </>
              ) : (
                "Record override"
              )}
            </Button>
            <Button type="button" variant="secondary" disabled={pending} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
